import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { Navigate, useParams } from 'react-router-dom';

import BrainHiLogo from '../../common/images/brainhi_logo_primary.svg';
import { updatePassword } from '../api';

const STATUS = {
  SUCCESS: 0,
  ERROR: 1,
};

type FormType = {
  password: string;
  confirmPassword: string;
};

const ConfirmResetPassword: React.FC = () => {
  const [resetConfirmStatus, setResetConfirmStatus] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
  } = useForm<FormType>();
  const { token } = useParams();

  const handleConfirmPasswordRest = async (data: FormType): Promise<void> => {
    try {
      await updatePassword(token, data.password);
      setResetConfirmStatus(STATUS.SUCCESS);
    } catch (e) {
      setResetConfirmStatus(STATUS.ERROR);
    }
  };

  if (resetConfirmStatus === STATUS.SUCCESS) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="flex flex-col">
      <img className="w-auto h-12 mx-auto" src={BrainHiLogo} alt="BrainHi" />
      <h2 className="mx-auto mt-6">Enter your new password</h2>
      <form className="flex flex-col mt-6" onSubmit={handleSubmit(handleConfirmPasswordRest)}>
        <label htmlFor="password" className="input-label">
          New Password
          <input
            className="input sm:text-sm w-full mt-1 transition duration-150 ease-in-out appearance-none"
            type="password"
            maxLength={100}
            {...register('password', { required: 'You must enter a password' })}
            data-testid="password"
          />
        </label>
        {errors.password && (
          <p className="error-text" data-testid="password-error">
            {errors.password.message}
          </p>
        )}
        <label htmlFor="confirmPassword" className="input-label">
          Confirm Password
          <input
            className="input sm:text-sm w-full mt-1 transition duration-150 ease-in-out appearance-none"
            type="password"
            maxLength={100}
            {...register('confirmPassword', {
              required: 'You must enter a password',
              validate: (value) => {
                if (value === getValues().password) {
                  return true;
                }
                return 'The passwords do not match';
              },
            })}
            data-testid="confirm-password"
          />
        </label>
        {errors.confirmPassword && (
          <p className="error-text" data-testid="confirm-password-error">
            {errors.confirmPassword.message}
          </p>
        )}
        <div className="text-gray-3.0 font-semibold text-xs mb-8 ml-4">
          <ul className="list-disc">
            <li>Your password can NOT be too similar to your username.</li>
            <li>Your password MUST contain at least 8 characters.</li>
            <li>Your password can NOT be entirely numeric.</li>
          </ul>
        </div>
        {resetConfirmStatus === STATUS.ERROR ? (
          <div className="text-red-1.0 mb-4 text-sm flex justify-center">
            There was a problem resetting your password. (Is the new password strong enough?)
          </div>
        ) : null}
        {resetConfirmStatus === STATUS.SUCCESS ? (
          <div className="text-primary-0.0 text-sm flex justify-center" data-testid="reset-success">
            Your password has been reset successfully.
          </div>
        ) : null}
        <button
          color="secondary"
          className="btn btn-secondary"
          type="submit"
          data-testid="reset-password"
        >
          RESET MY PASSWORD
        </button>
      </form>
    </div>
  );
};

export default ConfirmResetPassword;
